import { useCallback, useEffect, useRef, useState } from "react";
import { customAlphabet } from "nanoid";
import { useShowToast } from "@meiden-connect/ui";
import {
  CANVAS_HEIGHT_PX,
  CANVAS_WIDTH_PX,
  clampFrameToCanvas,
  clampPointToCanvas,
  isPointInsideCanvas,
  type CanvasObject,
} from "@refinement-canvas/domain";
import { useCanvasStore } from "./store/canvas-store";
import { CanvasGrid } from "./canvas-grid";
import { CanvasObjectsLayer } from "./canvas-objects-layer";
import { CanvasPenLayer, type PenLayerHandle } from "./canvas-pen-layer";
import { CanvasCursorsLayer } from "./canvas-cursors-layer";
import { CanvasSelectionOverlay } from "./canvas-selection-overlay";
import { CanvasAlignmentGuidesLayer } from "./canvas-alignment-guides-layer";
import { CanvasConnectorOverlay } from "./canvas-connector-overlay";
import { CanvasMarqueeOverlay } from "./canvas-marquee-overlay";
import { CanvasShapePlacementOverlay } from "./canvas-shape-placement-overlay";
import { CanvasRemoteSelectionsLayer } from "./canvas-remote-selections-layer";
import { CanvasRemotePreviewsLayer } from "./canvas-remote-previews-layer";
import { CANVAS_SNAP_PX } from "../../lib/canvas-coordinates";
import { useKeyboardPan } from "./input/use-keyboard-pan";
import { useWheelPanZoom } from "./input/use-wheel-pan-zoom";
import { usePasteHandler } from "./input/use-paste-handler";
import {
  PENDING_ATTACHMENT_SRC,
  generateImageLqipDataUrl,
  isTransientAttachmentSrc,
} from "./attachment-placeholder";
import { describeUploadError, putToPresignedUrl, validateVideoUpload } from "./upload-limits";
import { prepareImageForUpload } from "./image-resize";
import { useDropHandler } from "./input/use-drop-handler";
import { usePointerMode } from "./input/use-pointer-mode";
import { useMarqueeSelection } from "./input/use-marquee-selection";
import { useGlobalShortcuts } from "./input/use-global-shortcuts";
import {
  clearCanvasHistory,
  type CanvasObjectDraft,
  type CanvasOpDispatcher,
} from "./use-canvas-op-dispatcher";
import { cloneObjectAsDraft } from "../../lib/canvas-object-clone";
import { useCursorBroadcaster } from "./use-cursor-broadcaster";
import { useCanvasApi, type CanvasApiClient } from "../../lib/canvas-api";
import type { StrokePoint } from "../../lib/pen-stroke";
import { loadCanvasView, saveCanvasView } from "../../lib/canvas-view-preference";
import {
  buildCanvasViewSearchString,
  deriveCenterStageFromView,
  deriveViewFromCenterStage,
  readCanvasViewFromUrl,
} from "../../lib/canvas-view-url";
import { SHAPE_FILL_NONE } from "../../lib/shape-preference";
import type { ShapeDragFrame } from "../../lib/shape-arrow";

interface Props {
  readonly canvasId: string;
  readonly dispatcher: CanvasOpDispatcher;
  readonly currentUserId: string;
}

const STICKY_DEFAULT_W = 200;
const STICKY_DEFAULT_H = 160;
const TEXT_DEFAULT_W = 240;
const TEXT_DEFAULT_H = 48;
const SHAPE_DEFAULT_W = 160;
const SHAPE_DEFAULT_H = 120;
// 図形の既定の輪郭色（shape-object.tsx の SHAPE_DEFAULT_STROKE と一致させる）。
const SHAPE_STROKE_COLOR = "#1a1a2e";
// スタンプは正方形の小さめサイズで配置し、利用者がリサイズで大きさを調整する想定。
const STAMP_DEFAULT_SIZE = 96;
const IMAGE_DEFAULT_MAX = 480;
// 初回表示（共有 URL / 保存位置が無い場合）で使う既定ズーム倍率。
const DEFAULT_ZOOM = 1;
// パン/ズーム操作が止まったとみなすまでのアイドル時間（ms）。これを過ぎたら stage の
// will-change を解除し、現在のズーム倍率で再ラスタライズさせて文字・図形を鮮明に表示する。
const VIEW_INTERACTION_IDLE_MS = 200;
type ShowToast = ReturnType<typeof useShowToast>;

// 10240x5760 サイズの DOM ステージ。
// CSS transform: translate3d(panX,panY,0) scale(zoom) で全オブジェクトを一括変形する。
// 子レイヤはステージ座標で配置し、ヒットテストは DOM のイベントバブリングで処理する。
export function CanvasStage({ canvasId, dispatcher, currentUserId }: Props): JSX.Element {
  const view = useCanvasStore((s) => s.view);
  const tool = useCanvasStore((s) => s.tool);
  const shapeKind = useCanvasStore((s) => s.shapeKind);
  const shapeFill = useCanvasStore((s) => s.shapeFill);
  const stickyColor = useCanvasStore((s) => s.stickyColor);
  const stampKind = useCanvasStore((s) => s.stampKind);
  const textFontSize = useCanvasStore((s) => s.textFontSize);
  const textAlign = useCanvasStore((s) => s.textAlign);
  const setView = useCanvasStore((s) => s.setView);
  const containerRef = useRef<HTMLDivElement>(null);
  const penLayerRef = useRef<PenLayerHandle | null>(null);
  const showToast = useShowToast();
  // view を URL クエリ / localStorage へ同期して良いか（=利用者が能動的に表示を動かしたか）を保持する。
  // 初回表示（プログラムによる復元・中央寄せ）だけでは同期せず、利用者が pan/zoom した時点で
  // 同期を開始する。これにより、(1) 新規作成直後の URL を `/c/<id>` のまま汚さない、
  // (2) 一時的な既定 view が localStorage に保存されて中央寄せ初期表示を打ち消す事故を防ぐ。
  // 共有 URL（?cx/cy/z 付き）からの遷移時も、URL には既に位置が載っているため初回同期は不要で、
  // 利用者が実際に pan/zoom した時点で armed 化される（全経路で同じ扱いにする）。
  const viewSyncArmedRef = useRef(false);

  // パン/ズーム操作中かどうか。操作中だけ stage を GPU 合成レイヤへ昇格させ、静止後に解除する。
  const [isViewInteracting, setIsViewInteracting] = useState(false);
  const interactionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // state と二重持ちする操作中フラグ（effect 内で同期参照するため）と、最後に view が変化した時刻。
  const interactingRef = useRef(false);
  const lastViewChangeAtRef = useRef(0);

  const canvasApi = useCanvasApi();

  useEffect(() => {
    clearCanvasHistory();
    // canvas 切替時は同期可否をリセットする。CanvasStage は canvasId だけ変わっても同一
    // インスタンスで使い回されるため、前のキャンバスで armed になった状態を持ち越すと、
    // 次のキャンバスの初回プログラム的 setView まで即同期されてしまう。ここで必ず false に戻す。
    viewSyncArmedRef.current = false;
    // URL のクエリ (?cx=...&cy=...&z=...) が指定されていれば共有 URL からの遷移とみなして優先採用する。
    // 無ければ従来通り localStorage の最終表示位置を復元する。
    const fromUrl = readCanvasViewFromUrl(window.location.search);
    if (fromUrl) {
      // 共有 URL は既に位置を保持しているため、復元時点では同期しない（armed のまま false）。
      // 利用者が実際に pan/zoom した時点で armed 化され、以降の同期が始まる。
      // ここで先に armed=true にすると、復元 view 反映前の同一フラッシュで古い view を
      // 1 度だけ保存/同期してしまうため、明示的に armed 化しない。
      const restored = deriveViewFromCenterStage(
        fromUrl.centerStageX,
        fromUrl.centerStageY,
        fromUrl.zoom,
        { width: window.innerWidth, height: window.innerHeight }
      );
      setView(restored);
      return;
    }
    const saved = loadCanvasView(canvasId);
    if (saved) {
      setView(saved);
      return;
    }
    // 共有 URL も保存済み表示位置も無い「初回表示」では、左上原点ではなくキャンバス中央を
    // 画面中央に合わせて表示する（広大なキャンバスの中心から作業を始められるようにする）。
    setView(
      deriveViewFromCenterStage(CANVAS_WIDTH_PX / 2, CANVAS_HEIGHT_PX / 2, DEFAULT_ZOOM, {
        width: window.innerWidth,
        height: window.innerHeight,
      })
    );
  }, [canvasId, setView]);

  // 利用者が実際に「表示（view）を動かす」操作でのみ view 同期を有効化する。
  // 初回表示のプログラム的な setView や、アバタークリック等の UI 操作（→フォーカスモードの
  // プログラム的 setView）では同期しない。対象は次に限定する:
  //   - キャンバス本体上のホイール（pan/zoom）
  //   - キャンバス本体上の pointerdown（空白ドラッグでの pan 等）
  //   - WASD / カーソルキー（キーボード pan。修飾キー併用・入力要素フォーカス時は除外）
  // canvasId が変わると上の復元 effect が armed を false に戻すため、ここも canvasId 依存で
  // リスナを貼り直し、新しいキャンバスで再び arm 判定できるようにする。
  useEffect(() => {
    if (viewSyncArmedRef.current) return undefined;
    const arm = (): void => {
      viewSyncArmedRef.current = true;
    };
    const isInsideCanvas = (target: EventTarget | null): boolean =>
      target instanceof Node && !!containerRef.current?.contains(target);
    const onWheel = (event: WheelEvent): void => {
      if (isInsideCanvas(event.target)) arm();
    };
    const onPointerDown = (event: PointerEvent): void => {
      if (isInsideCanvas(event.target)) arm();
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      // WASD に加えてカーソルキーも view 同期の arm 対象にする（use-keyboard-pan と対応）。
      const PAN_KEYS = ["w", "a", "s", "d", "arrowup", "arrowdown", "arrowleft", "arrowright"];
      if (!PAN_KEYS.includes(event.key.toLowerCase())) return;
      const t = event.target;
      if (
        t instanceof HTMLElement &&
        (t.isContentEditable || ["input", "textarea", "select"].includes(t.tagName.toLowerCase()))
      ) {
        return;
      }
      arm();
    };
    window.addEventListener("wheel", onWheel, { capture: true, passive: true });
    window.addEventListener("pointerdown", onPointerDown, { capture: true });
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => {
      window.removeEventListener("wheel", onWheel, { capture: true });
      window.removeEventListener("pointerdown", onPointerDown, { capture: true });
      window.removeEventListener("keydown", onKeyDown, { capture: true });
    };
  }, [canvasId]);

  useEffect(() => {
    // 利用者が能動的に表示を動かすまでは保存しない（初回の既定 view を保存して中央寄せを
    // 打ち消す事故を防ぐ）。
    if (!viewSyncArmedRef.current) return;
    saveCanvasView(canvasId, view);
  }, [canvasId, view]);

  // view 変更を URL クエリへリアルタイム反映する（共有 URL 用）。
  // pan/zoom は短時間で多数発火するため、replaceState の連打を抑えるよう rAF 一括にまとめる。
  useEffect(() => {
    // 利用者が能動的に表示を動かすまでは URL を書き換えない。
    // これにより新規作成直後の URL は `/c/<id>` のまま保たれ、共有時のみ ?cx/cy/z が付く。
    if (!viewSyncArmedRef.current) return undefined;
    const handle = window.requestAnimationFrame(() => {
      const center = deriveCenterStageFromView(view, {
        width: window.innerWidth,
        height: window.innerHeight,
      });
      const nextSearch = buildCanvasViewSearchString(window.location.search, {
        centerStageX: center.centerStageX,
        centerStageY: center.centerStageY,
        zoom: view.zoom,
      });
      const nextUrl = `${window.location.pathname}${nextSearch}${window.location.hash}`;
      // pushState だと履歴が肥大化するため replaceState で現在エントリのみを置換する。
      // @meiden-connect/ui のルータ互換レイヤは history.state に独自値が入ることを防ぐため、
      // state は null/undefined であることを要求する (router.tsx の pushState(null, ...) と同様)。
      // 現在の history.state をそのまま渡すと AssertionError ('state' must be null or undefined)
      // となるため、第 1 引数は null にする。
      window.history.replaceState(null, "", nextUrl);
    });
    return () => window.cancelAnimationFrame(handle);
  }, [view]);

  // アンマウント時に will-change 監視タイマーを確実に破棄する（view effect 側はタイマーを
  // 跨いで維持するため cleanup を持たない。破棄はこのマウント専用 effect に集約する）。
  useEffect(() => {
    return () => {
      if (interactionTimerRef.current) {
        clearTimeout(interactionTimerRef.current);
        interactionTimerRef.current = null;
      }
    };
  }, []);

  // 文字・図形のにじみ（要望③）対策。
  // stage は `transform: scale(zoom)` で拡大する。will-change: transform を恒久指定すると、
  // ブラウザはレイヤを 1 度だけラスタライズしてビットマップを GPU 拡大するため、拡大時に
  // 文字や図形（DOM / SVG なのに）がぼやけて見える。そこで、view（pan/zoom）が変化している
  // 間だけ will-change: transform を有効化して合成を滑らかにし、操作が止まったら will-change
  // を解除する。解除によりブラウザが現在のズーム倍率で再ラスタライズし、ベクター本来の
  // 鮮明さで表示される（ZOOM_MAX=16 まで拡大できるため効果が大きい）。
  //
  // 性能配慮: キーボードパン等で view は毎フレーム更新されるため、操作中は state もタイマーも
  // 再設定せず（最後の操作時刻 lastViewChangeAtRef だけ更新する）、単一の監視タイマーが
  // 「最後の操作から VIEW_INTERACTION_IDLE_MS 経過」を自己再スケジュールで検知する。これにより
  // 毎フレームの clearTimeout/setTimeout を避ける。タイマーが消えている場合（StrictMode の
  // 再マウント等）は再武装する。
  useEffect(() => {
    lastViewChangeAtRef.current = Date.now();
    if (interactingRef.current && interactionTimerRef.current !== null) return;
    interactingRef.current = true;
    setIsViewInteracting(true);
    const watchdog = (): void => {
      const idle = Date.now() - lastViewChangeAtRef.current;
      if (idle >= VIEW_INTERACTION_IDLE_MS) {
        interactionTimerRef.current = null;
        interactingRef.current = false;
        setIsViewInteracting(false);
      } else {
        interactionTimerRef.current = setTimeout(watchdog, VIEW_INTERACTION_IDLE_MS - idle);
      }
    };
    if (interactionTimerRef.current) clearTimeout(interactionTimerRef.current);
    interactionTimerRef.current = setTimeout(watchdog, VIEW_INTERACTION_IDLE_MS);
  }, [view]);

  // ペン描画中: 増分点を他参加者へ一時 op として中継する（ゴースト表示用）。
  const onStrokeProgress = useCallback(
    ({ strokeId, points }: { strokeId: string; points: readonly StrokePoint[] }) => {
      dispatcher.previewPenAppend(strokeId, points);
    },
    [dispatcher]
  );

  // ペンストローク確定時: 描画範囲を AABB に丸めて pen.stroke.commit op を送信。
  // 描画中の transient と同じ strokeId を確定オブジェクト ID に採用し、確定時に該当
  // ストロークのゴーストだけを正確に破棄できるようにする。
  const onStrokeCommit = useCallback(
    (params: {
      strokeId: string;
      points: readonly StrokePoint[];
      color: string;
      thickness: number;
    }) => {
      const clippedPoints = params.points.map((point) => clampPointToCanvas(point));
      const aabb = computeAabb(clippedPoints, params.thickness);
      dispatcher.commitPenStroke(
        clippedPoints,
        params.color,
        params.thickness,
        aabb,
        params.strokeId
      );
    },
    [dispatcher]
  );

  // 付箋 / 図形 / テキストの配置。
  // グリッド吸着後にキャンバス内へ clamp し、既存の吸着挙動と境界制約を両立する。
  const onPlaceObject = useCallback(
    ({ stagePoint }: { stagePoint: { x: number; y: number } }) => {
      if (!isPointInsideCanvas(stagePoint)) {
        showCanvasBoundsToast(showToast);
        return;
      }
      switch (tool) {
        case "place-sticky": {
          const p = snapPointIfGridEnabled({
            x: stagePoint.x - STICKY_DEFAULT_W / 2,
            y: stagePoint.y - STICKY_DEFAULT_H / 2,
          });
          const frame = clampFrameToCanvas({
            x: p.x,
            y: p.y,
            w: STICKY_DEFAULT_W,
            h: STICKY_DEFAULT_H,
          });
          dispatcher.createObject({
            type: "sticky",
            ...frame,
            text: "",
            // stickyColor は hex 文字列をそのまま保持する（pen と同じ方針）。
            color: stickyColor,
            // 挿入前に選択した文字寄せを初期値として保持する（既定は左寄せ）。
            textAlign,
          });
          return;
        }
        case "place-text": {
          const p = snapPointIfGridEnabled(stagePoint);
          // 文字サイズに応じて初期の高さを概算で広げ、極端な大型サイズで初期描画領域が
          // 不足して読みづらくならないようにする（最低でも 1 行分は確保する）。
          const initialHeight = Math.max(TEXT_DEFAULT_H, Math.ceil(textFontSize * 1.6));
          const frame = clampFrameToCanvas({
            x: p.x,
            y: p.y,
            w: TEXT_DEFAULT_W,
            h: initialHeight,
          });
          dispatcher.createObject({
            type: "text",
            ...frame,
            text: "テキスト",
            fontSize: textFontSize,
            // 挿入前に選択した文字寄せを初期値として保持する（既定は左寄せ）。
            textAlign,
          });
          return;
        }
        case "place-shape": {
          const p = snapPointIfGridEnabled({
            x: stagePoint.x - SHAPE_DEFAULT_W / 2,
            y: stagePoint.y - SHAPE_DEFAULT_H / 2,
          });
          const frame = clampFrameToCanvas({
            x: p.x,
            y: p.y,
            w: SHAPE_DEFAULT_W,
            h: SHAPE_DEFAULT_H,
          });
          dispatcher.createObject({
            type: "shape",
            shapeKind,
            ...frame,
            stroke: SHAPE_STROKE_COLOR,
            // 背景色（fill）は丸 / 四角で「背景なし」以外を選んでいるときだけ設定する。
            // 矢印は塗りつぶせる領域を持たないため fill を付けない。
            ...(shapeKind !== "arrow" && shapeFill !== SHAPE_FILL_NONE ? { fill: shapeFill } : {}),
          });
          return;
        }
        case "place-stamp": {
          const p = snapPointIfGridEnabled({
            x: stagePoint.x - STAMP_DEFAULT_SIZE / 2,
            y: stagePoint.y - STAMP_DEFAULT_SIZE / 2,
          });
          const frame = clampFrameToCanvas({
            x: p.x,
            y: p.y,
            w: STAMP_DEFAULT_SIZE,
            h: STAMP_DEFAULT_SIZE,
          });
          dispatcher.createObject({
            type: "stamp",
            stampKind,
            ...frame,
          });
          return;
        }
        default:
          return;
      }
    },
    [
      dispatcher,
      shapeFill,
      shapeKind,
      showToast,
      stampKind,
      stickyColor,
      textAlign,
      textFontSize,
      tool,
    ]
  );

  // 図形をドラッグで任意サイズ配置したときの確定。frame は use-pointer-mode 側で
  // グリッド吸着・最小サイズ・キャンバス範囲内 clamp を反映済み。矢印のときだけ向き
  // （arrowFromCorner）を載せ、丸 / 四角では向きを使わない。
  const onPlaceShapeDrag = useCallback(
    ({ frame }: { frame: ShapeDragFrame }) => {
      dispatcher.createObject({
        type: "shape",
        shapeKind,
        x: frame.x,
        y: frame.y,
        w: frame.w,
        h: frame.h,
        stroke: SHAPE_STROKE_COLOR,
        // 矢印は塗りつぶせる領域を持たないため fill を付けない（丸 / 四角のみ背景色）。
        ...(shapeKind !== "arrow" && shapeFill !== SHAPE_FILL_NONE ? { fill: shapeFill } : {}),
        ...(shapeKind === "arrow" ? { arrowFromCorner: frame.arrowFromCorner } : {}),
      });
    },
    [dispatcher, shapeFill, shapeKind]
  );

  // クリップボード画像: presigned PUT でアップロードしてから image オブジェクトを作成
  const onPasteImage = useCallback(
    (file: File, position: { x: number; y: number }) => {
      void uploadAndPlaceImage(canvasApi, canvasId, dispatcher, file, position, showToast);
    },
    [canvasApi, canvasId, dispatcher, showToast]
  );

  // クリップボード URL: OGP 取得してリンクカード化
  const onPasteUrl = useCallback(
    (url: string, position: { x: number; y: number }) => {
      if (!isPlacementPointAvailable(position, showToast)) return;
      void (async () => {
        const p = snapPointIfGridEnabled(position);
        try {
          const og = await canvasApi.fetchOgp(url);
          const frame = clampFrameToCanvas({ x: p.x, y: p.y, w: 320, h: 200 });
          dispatcher.createObject({
            type: "link-card",
            ...frame,
            url,
            title: og.title ?? url,
            description: og.description,
            imageUrl: og.image,
            siteName: og.siteName,
          });
        } catch {
          // OGP 取得失敗時は URL テキストとして配置
          const frame = clampFrameToCanvas({
            x: p.x,
            y: p.y,
            w: TEXT_DEFAULT_W,
            h: TEXT_DEFAULT_H,
          });
          dispatcher.createObject({
            type: "text",
            ...frame,
            text: url,
            color: "#1e63d4",
          });
        }
      })();
    },
    [canvasApi, dispatcher, showToast]
  );

  const onDropImage = useCallback(
    (file: File, position: { x: number; y: number }) => {
      void uploadAndPlaceImage(canvasApi, canvasId, dispatcher, file, position, showToast);
    },
    [canvasApi, canvasId, dispatcher, showToast]
  );

  const onPasteCanvasObjects = useCallback(
    (objects: readonly CanvasObject[], position: { x: number; y: number }) => {
      pasteCanvasObjects(objects, position, dispatcher, showToast);
    },
    [dispatcher, showToast]
  );

  const onDropVideo = useCallback(
    (file: File, position: { x: number; y: number }) => {
      void uploadAndPlaceVideo(canvasApi, canvasId, dispatcher, file, position, showToast);
    },
    [canvasApi, canvasId, dispatcher, showToast]
  );

  // 入力ハンドラを束ねる
  useGlobalShortcuts(dispatcher);
  useKeyboardPan();
  useWheelPanZoom(containerRef);
  useDropHandler(containerRef, { onDropImage, onDropVideo });
  usePasteHandler({ onPasteCanvasObjects, onPasteImage, onPasteUrl });
  usePointerMode(
    { stageRef: containerRef, penLayerRef },
    { onStrokeCommit, onStrokeProgress, onPlaceObject, onPlaceShapeDrag }
  );
  // 右ボタンドラッグでの範囲指定選択。左ボタン系（パン/ドラッグ/描画/配置）と排他。
  useMarqueeSelection(containerRef);
  useCursorBroadcaster();

  return (
    <div
      ref={containerRef}
      data-testid="canvas-container"
      className={
        tool === "panning"
          ? "canvas-cursor-grabbing"
          : tool === "draw-pen"
            ? "canvas-cursor-crosshair"
            : tool === "place-sticky" ||
                tool === "place-shape" ||
                tool === "place-text" ||
                tool === "place-stamp"
              ? "canvas-cursor-crosshair"
              : "canvas-cursor-default"
      }
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        touchAction: "none",
      }}
    >
      <div
        data-testid="canvas-stage"
        style={{
          position: "absolute",
          width: `${CANVAS_WIDTH_PX}px`,
          height: `${CANVAS_HEIGHT_PX}px`,
          transformOrigin: "0 0",
          transform: `translate3d(${view.panX}px, ${view.panY}px, 0) scale(${view.zoom})`,
          // パン/ズーム操作中のみ合成レイヤへ昇格。静止時は auto に戻して再ラスタライズさせ、
          // 拡大時の文字・図形のにじみを防ぐ（要望③）。
          willChange: isViewInteracting ? "transform" : "auto",
          background: "var(--color-surface)",
          boxShadow: "0 0 0 1px var(--color-border)",
          pointerEvents: "none",
        }}
      >
        <CanvasGrid />
        <CanvasObjectsLayer />
        <CanvasRemotePreviewsLayer currentUserId={currentUserId} />
        <CanvasRemoteSelectionsLayer currentUserId={currentUserId} />
        <CanvasSelectionOverlay />
        <CanvasAlignmentGuidesLayer />
        <CanvasConnectorOverlay />
        <CanvasMarqueeOverlay />
        <CanvasShapePlacementOverlay />
        <CanvasPenLayer handleRef={penLayerRef} />
        <CanvasCursorsLayer currentUserId={currentUserId} />
      </div>
    </div>
  );
}

// 貼り付け複製で先に id を確定するためのジェネレータ（dispatcher と同じ文字種・長さ）。
// コネクタの端点 id を新しい複製先へ貼り替えるため、createObjects へ渡す前に id を決める必要がある。
const generatePastedObjectId = customAlphabet(
  "23456789abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ",
  16
);

function pasteCanvasObjects(
  objects: readonly CanvasObject[],
  position: { x: number; y: number },
  dispatcher: CanvasOpDispatcher,
  showToast: ShowToast
): void {
  if (objects.length === 0) return;
  if (!isPlacementPointAvailable(position, showToast)) return;
  // アップロード未完了の画像・動画（blob:/data: src）は貼り付け対象から除外する。
  // そのまま複製すると公開 URL を持たない object.create が再配信され、他参加者で
  // 解決できない画像や永続的な仮表示になるため（コピー側の除外と二重化）。
  // 混在ペイロードでは未完了添付だけを除外し、確定済みのオブジェクトは通常通り貼り付ける。
  const attachmentReady = objects.filter((object) => !isTransientAttachmentObject(object));
  // 未完了添付（画像・動画）を除外した件数。トーストはこの理由のときだけ出す。
  const attachmentExcludedCount = objects.length - attachmentReady.length;
  // コネクタ（接続矢印）は両端の図形が同じ貼り付け対象に含まれるときだけ複製する。
  // 端点 id を新しく複製した図形の id へ貼り替えるため、片方しか含まれない（＝既存図形や
  // 別キャンバスの図形を指す）コネクタは宙ぶらりんになるので除外する（端点未選択時の自然な挙動
  // のため、これ自体はトーストしない）。
  const attachmentReadyIds = new Set(attachmentReady.map((object) => object.id));
  const placeable = attachmentReady.filter(
    (object) =>
      object.type !== "connector" ||
      (attachmentReadyIds.has(object.from.objectId) && attachmentReadyIds.has(object.to.objectId))
  );
  if (placeable.length === 0) {
    // 貼り付けできるものが無い。理由が未完了添付なら案内し、コネクタのみ（端点未選択）なら静かに終える。
    if (attachmentExcludedCount > 0) {
      showToast(
        "warning",
        "アップロード完了前の画像・動画は貼り付けできません。完了後にもう一度お試しください。"
      );
    }
    return;
  }
  // 貼り付け対象全体の外接矩形を求め、マウス位置へ中心合わせで複製する。
  const bounds = placeable.reduce(
    (acc, object) => ({
      left: Math.min(acc.left, object.x),
      top: Math.min(acc.top, object.y),
      right: Math.max(acc.right, object.x + object.w),
      bottom: Math.max(acc.bottom, object.y + object.h),
    }),
    { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity }
  );
  const boundsWidth = bounds.right - bounds.left;
  const boundsHeight = bounds.bottom - bounds.top;
  if (boundsWidth > CANVAS_WIDTH_PX || boundsHeight > CANVAS_HEIGHT_PX) {
    showToast(
      "warning",
      "貼り付け対象がキャンバスより大きいため作成できません。新しいキャンバスへの分割をご検討ください。"
    );
    return;
  }
  const rawOffsetX = position.x - (bounds.left + bounds.right) / 2;
  const rawOffsetY = position.y - (bounds.top + bounds.bottom) / 2;
  // グリッド吸着 ON のときは「貼り付け後の左上」を 10px グリッドに揃える。
  // 中央値ではなく左上で丸めることで複数オブジェクトの相対位置をそのまま保つ。
  const topLeft = snapPointIfGridEnabled({
    x: bounds.left + rawOffsetX,
    y: bounds.top + rawOffsetY,
  });
  let offsetX = topLeft.x - bounds.left;
  let offsetY = topLeft.y - bounds.top;
  const clampedBounds = clampFrameToCanvas({
    x: bounds.left + offsetX,
    y: bounds.top + offsetY,
    w: boundsWidth,
    h: boundsHeight,
  });
  offsetX += clampedBounds.x - (bounds.left + offsetX);
  offsetY += clampedBounds.y - (bounds.top + offsetY);
  // 旧 id → 複製後の新 id の対応表。コネクタの端点 id を貼り替えるため、createObjects へ
  // 渡す前に各オブジェクトの新 id を確定しておく。
  const idMap = new Map(placeable.map((object) => [object.id, generatePastedObjectId()]));
  // コネクタは端点図形より後に create する必要がある（サーバは create 時に端点の存在を検証し、
  // まだ作成されていない端点を参照するコネクタを reject するため）。端点 → コネクタの順に並べる。
  const endpointDrafts: CanvasObjectDraft[] = [];
  const connectorDrafts: CanvasObjectDraft[] = [];
  for (const object of placeable) {
    const stripped = cloneObjectAsDraft(object, { x: offsetX, y: offsetY });
    const newId = idMap.get(object.id)!;
    if (object.type === "connector") {
      // 端点 id を新しい複製先へ貼り替える（両端が placeable に含まれることは上で保証済み）。
      connectorDrafts.push({
        ...stripped,
        id: newId,
        x: object.x + offsetX,
        y: object.y + offsetY,
        from: { ...object.from, objectId: idMap.get(object.from.objectId)! },
        to: { ...object.to, objectId: idMap.get(object.to.objectId)! },
      } as CanvasObjectDraft);
      continue;
    }
    endpointDrafts.push({
      ...stripped,
      id: newId,
      x: object.x + offsetX,
      y: object.y + offsetY,
    });
  }
  const pastedIds = dispatcher
    .createObjects([...endpointDrafts, ...connectorDrafts])
    .map((object) => object.id);
  useCanvasStore.getState().setSelectedObjectIds(pastedIds);
  if (attachmentExcludedCount > 0) {
    showToast("info", "アップロード完了前の画像・動画は貼り付け対象から除外しました。");
  }
}

// アップロード未完了の画像・動画（blob:/data: src）かどうか。貼り付け複製の除外判定に使う。
function isTransientAttachmentObject(object: CanvasObject): boolean {
  return (
    (object.type === "image" || object.type === "video") && isTransientAttachmentSrc(object.src)
  );
}

function isPlacementPointAvailable(
  point: { readonly x: number; readonly y: number },
  showToast: ShowToast
): boolean {
  if (isPointInsideCanvas(point)) return true;
  showCanvasBoundsToast(showToast);
  return false;
}

function showCanvasBoundsToast(showToast: ShowToast): void {
  showToast("warning", "キャンバス範囲外には新しいオブジェクトを作成できません。");
}

/**
 * ストローク座標群と太さから外接矩形を求める。pen-stroke オブジェクトの x,y,w,h に使う。
 */
function computeAabb(
  points: readonly { x: number; y: number }[],
  thickness: number
): { x: number; y: number; w: number; h: number } {
  if (points.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  const first = points[0];
  if (!first) return { x: 0, y: 0, w: 0, h: 0 };
  let minX = first.x;
  let minY = first.y;
  let maxX = first.x;
  let maxY = first.y;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  const pad = thickness;
  return {
    x: minX - pad,
    y: minY - pad,
    w: maxX - minX + pad * 2,
    h: maxY - minY + pad * 2,
  };
}

/**
 * 画像ファイルを「先に仮表示、あとから本体同期」で配置する。
 *
 * 1) 作成者にはローカル blob でフル画質を即表示しつつ、他参加者へは超圧縮 LQIP を src にした
 *    create を即配信する（誰かが画像を置いたことと、ぼやけた仮プレビューがすぐ伝わる）。
 *    blob: URL は他参加者が解決できないため、create op には絶対に載せない。
 * 2) presigned PUT で S3 にバイナリ送信
 * 3) /uploads:complete を叩き、CloudFront 公開 URL を取得（未設定なら null）
 * 4) publicUrl が取れたら attachment.update op で全員を公開 URL 表示へ収束させる
 *    publicUrl が null（dev/mock で共有不可）の場合は仮表示のまま据え置く
 *    アップロード失敗時は仮表示を全員から取り除き、トーストで通知する
 */
async function uploadAndPlaceImage(
  canvasApi: CanvasApiClient,
  canvasId: string,
  dispatcher: CanvasOpDispatcher,
  file: File,
  position: { x: number; y: number },
  showToast: ShowToast
): Promise<void> {
  if (!isPlacementPointAvailable(position, showToast)) return;
  // 楽観更新: まず元ファイルで仮表示（プレースホルダ）を即時作成する（AGENTS の楽観更新方針。
  // 大きい画像の自動縮小・変換に時間がかかっても「無反応」に見せない）。
  const placed = await placeImagePlaceholder(dispatcher, file, position);
  if (!placed) return;
  const { objectId, overrideOpId, localUrl } = placed;
  // アップロード前処理: 上限超過なら自動で縮小し、非対応形式でもデコード可能なら対応形式へ自動変換する。
  // デコード不能・縮小しても収まらない場合は、仮表示を取り消して原因の分かる文言で通知する。
  const prepared = await prepareImageForUpload(file);
  if (!prepared.ok) {
    dispatcher.discardAttachmentPlaceholder(objectId, overrideOpId);
    URL.revokeObjectURL(localUrl);
    showToast("error", prepared.message);
    return;
  }
  // 以降のアップロードは整形後のファイル（自動縮小・変換された場合は新しい File）を使う。
  const uploadFile = prepared.file;
  try {
    const presigned = await canvasApi.presignImageUpload({
      canvasId,
      objectId,
      contentType: uploadFile.type || "image/png",
      contentLength: uploadFile.size,
    });
    // S3 への直接 PUT。response.ok を検査して握り潰しを防ぐ（putToPresignedUrl 内で実施）。
    await putToPresignedUrl(presigned.uploadUrl, uploadFile, uploadFile.type || "image/png");
    const completed = await canvasApi.notifyUploadComplete(
      canvasId,
      presigned.bucket,
      presigned.key
    );
    if (completed.publicUrl) {
      dispatcher.finalizeAttachmentPlaceholder(objectId, overrideOpId, completed.publicUrl);
      URL.revokeObjectURL(localUrl);
    } else {
      // 公開 URL が無い環境（dev/mock）では共有できない。本人のローカルプレビューと
      // 他参加者側の仮表示はそのまま残す（localUrl は本人表示で使い続けるため revoke しない）。
      console.warn(
        "[upload] 公開 URL が未設定のため画像を共有できません（ATTACHMENTS_PUBLIC_BASE_URL 未設定）"
      );
    }
  } catch (e) {
    console.warn("[upload] 画像アップロードに失敗", e);
    dispatcher.discardAttachmentPlaceholder(objectId, overrideOpId);
    URL.revokeObjectURL(localUrl);
    showToast("error", describeUploadError("image", e));
  }
}

async function uploadAndPlaceVideo(
  canvasApi: CanvasApiClient,
  canvasId: string,
  dispatcher: CanvasOpDispatcher,
  file: File,
  position: { x: number; y: number },
  showToast: ShowToast
): Promise<void> {
  if (!isPlacementPointAvailable(position, showToast)) return;
  // プレースホルダ作成前に検証する。動画はクライアント変換できないため、ブラウザで再生可能な形式は
  // 全て受理し、再生不可・空・上限超過のみ原因の分かる専用メッセージで弾く（汎用トーストに潰さない）。
  const validation = validateVideoUpload(file);
  if (!validation.ok) {
    showToast("error", validation.message);
    return;
  }
  const placed = await placeVideoPlaceholder(dispatcher, file, position);
  if (!placed) return;
  const { objectId, overrideOpId, localUrl } = placed;
  try {
    const presigned = await canvasApi.presignVideoUpload({
      canvasId,
      objectId,
      contentType: file.type || "video/mp4",
      contentLength: file.size,
    });
    // 画像と同様、putToPresignedUrl 内で response.ok を検査して握り潰しを防ぐ。
    await putToPresignedUrl(presigned.uploadUrl, file, file.type || "video/mp4");
    const completed = await canvasApi.notifyUploadComplete(
      canvasId,
      presigned.bucket,
      presigned.key
    );
    if (completed.publicUrl) {
      dispatcher.finalizeAttachmentPlaceholder(objectId, overrideOpId, completed.publicUrl);
      URL.revokeObjectURL(localUrl);
    } else {
      console.warn(
        "[upload] 公開 URL が未設定のため動画を共有できません（ATTACHMENTS_PUBLIC_BASE_URL 未設定）"
      );
    }
  } catch (e) {
    console.warn("[upload] 動画アップロードに失敗", e);
    dispatcher.discardAttachmentPlaceholder(objectId, overrideOpId);
    URL.revokeObjectURL(localUrl);
    showToast("error", describeUploadError("video", e));
  }
}

interface PlacedAttachment {
  readonly objectId: string;
  readonly overrideOpId: string;
  readonly localUrl: string;
}

async function placeImagePlaceholder(
  dispatcher: CanvasOpDispatcher,
  file: File,
  position: { x: number; y: number }
): Promise<PlacedAttachment | null> {
  const localUrl = URL.createObjectURL(file);
  // SVG は width/height 未指定（viewBox のみ）でも本来の縦横比を保てるよう、専用の寸法解決を使う。
  const dimensions =
    file.type === "image/svg+xml"
      ? await readSvgSize(file, localUrl)
      : await readImageSize(localUrl);
  const p = snapPointIfGridEnabled({
    x: position.x - dimensions.w / 2,
    y: position.y - dimensions.h / 2,
  });
  const frame = clampFrameToCanvas({
    x: p.x,
    y: p.y,
    w: dimensions.w,
    h: dimensions.h,
  });
  // 他参加者へ配信する仮表示用 src（超圧縮 LQIP。生成不可なら透明プレースホルダ）。
  const lqip = await generateImageLqipDataUrl(localUrl);
  // 永続化用の placeholderSrc は、有用な LQIP が生成できたときだけ設定する
  // （透明 1x1 プレースホルダは背景として無意味なため）。
  // サーバ永続化により、リロード後や別セッションの参加者でもぼかしフォールバックが利用できる。
  const persistedPlaceholder = lqip !== PENDING_ATTACHMENT_SRC ? lqip : undefined;
  const placed = dispatcher.createAttachmentPlaceholder(
    {
      type: "image",
      ...frame,
      src: lqip,
      alt: file.name,
      ...(persistedPlaceholder ? { placeholderSrc: persistedPlaceholder } : {}),
    },
    localUrl
  );
  if (!placed) {
    URL.revokeObjectURL(localUrl);
    return null;
  }
  return { objectId: placed.object.id, overrideOpId: placed.overrideOpId, localUrl };
}

async function placeVideoPlaceholder(
  dispatcher: CanvasOpDispatcher,
  file: File,
  position: { x: number; y: number }
): Promise<PlacedAttachment | null> {
  const localUrl = URL.createObjectURL(file);
  const p = snapPointIfGridEnabled({ x: position.x - 240, y: position.y - 135 });
  const frame = clampFrameToCanvas({
    x: p.x,
    y: p.y,
    w: 480,
    h: 270,
  });
  // 動画はフレーム抽出を行わず、他参加者へは透明プレースホルダ（ローディング枠）を配信する。
  const placed = dispatcher.createAttachmentPlaceholder(
    {
      type: "video",
      ...frame,
      src: PENDING_ATTACHMENT_SRC,
      mimeType: file.type || "video/mp4",
    },
    localUrl
  );
  if (!placed) {
    URL.revokeObjectURL(localUrl);
    return null;
  }
  return { objectId: placed.object.id, overrideOpId: placed.overrideOpId, localUrl };
}

// 固有サイズを持たない SVG（width/height 未指定）で naturalWidth/Height が 0 になった場合の既定サイズ。
const SVG_FALLBACK_SIZE = { w: 320, h: 240 } as const;

// SVG ファイルから固有の縦横比を読み取り、表示サイズ（最長辺 = IMAGE_DEFAULT_MAX）を返す。
// width/height 属性が無く viewBox のみの SVG でも、本来の縦横比を保ったまま配置できる。
// 解析に失敗した場合は通常の readImageSize（<img> の固有サイズ / 既定値）へフォールバックする。
async function readSvgSize(file: File, localUrl: string): Promise<{ w: number; h: number }> {
  try {
    const text = await file.text();
    const doc = new DOMParser().parseFromString(text, "image/svg+xml");
    const svg = doc.documentElement;
    if (
      svg.nodeName.toLowerCase() === "svg" &&
      doc.getElementsByTagName("parsererror").length === 0
    ) {
      const ratio = readSvgIntrinsicRatio(svg);
      if (ratio) {
        // ベクターは拡大しても劣化しないため、最長辺を IMAGE_DEFAULT_MAX に合わせて配置する。
        const scale = IMAGE_DEFAULT_MAX / Math.max(ratio.w, ratio.h);
        return { w: ratio.w * scale, h: ratio.h * scale };
      }
    }
  } catch {
    // パース失敗時はフォールバックする。
  }
  return readImageSize(localUrl);
}

// SVG 要素から固有の縦横（width/height 属性、無ければ viewBox の幅・高さ）を取り出す。
function readSvgIntrinsicRatio(svg: Element): { w: number; h: number } | null {
  const parseLength = (value: string | null): number | null => {
    if (!value) return null;
    // 単位なし or px のみを数値として扱う（%, em 等は固有サイズとして使わない）。
    const match = /^\s*([0-9]*\.?[0-9]+)\s*(px)?\s*$/i.exec(value);
    if (!match) return null;
    const n = Number.parseFloat(match[1] ?? "");
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const wAttr = parseLength(svg.getAttribute("width"));
  const hAttr = parseLength(svg.getAttribute("height"));
  if (wAttr !== null && hAttr !== null) return { w: wAttr, h: hAttr };
  const viewBox = svg.getAttribute("viewBox");
  if (viewBox) {
    const parts = viewBox.split(/[\s,]+/);
    const vw = Number.parseFloat(parts[2] ?? "");
    const vh = Number.parseFloat(parts[3] ?? "");
    if (Number.isFinite(vw) && Number.isFinite(vh) && vw > 0 && vh > 0) {
      return { w: vw, h: vh };
    }
  }
  return null;
}

function readImageSize(url: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // SVG で width/height が未指定だと naturalWidth/Height が 0 になることがある。
      // その場合は 0 サイズで作成されないよう既定サイズへフォールバックする。
      if (img.naturalWidth === 0 || img.naturalHeight === 0) {
        resolve({ ...SVG_FALLBACK_SIZE });
        return;
      }
      const ratio = Math.min(1, IMAGE_DEFAULT_MAX / Math.max(img.naturalWidth, img.naturalHeight));
      resolve({ w: img.naturalWidth * ratio, h: img.naturalHeight * ratio });
    };
    img.onerror = () => resolve({ w: 320, h: 240 });
    img.src = url;
  });
}

// グリッド吸着 ON のとき指定座標を CANVAS_SNAP_PX に丸める。OFF のときは何もしない。
// 貼り付け・ドロップ・OGP リンクカード・ペーストオブジェクトなど
// 「ペン以外の新規配置」全経路で同じ動きをさせるための共通ヘルパー。
function snapPointIfGridEnabled(point: { readonly x: number; readonly y: number }): {
  x: number;
  y: number;
} {
  if (!useCanvasStore.getState().gridSnapEnabled) return { x: point.x, y: point.y };
  return { x: snapToGrid(point.x), y: snapToGrid(point.y) };
}

function snapToGrid(value: number): number {
  return Math.round(value / CANVAS_SNAP_PX) * CANVAS_SNAP_PX;
}
